import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { isParent, Table, usePagination } from 'components/Table'
import { useSort } from 'components/Table/Sort'
import { calculatePagination } from 'providers/tanstack-query/table'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useColumns } from './useColumns'
import { useGetGetFinanceReport } from './useGetFinanceReport'

import type { FinanceReportRow, FinanceReportSortBy } from './useGetFinanceReport'
import type { CSSObject, Theme } from '@mui/material'
import type { ColumnDef, Row } from '@tanstack/react-table'
import type { PaginationProps } from 'components/Table/Pagination'

export const MonthlyEarningsSection = (): React.JSX.Element => {
  const { t } = useTranslation('features/finance', { keyPrefix: 'monthlyEarnings' })

  const { page, rowsPerPage, setPage, ...usePaginationProps } = usePagination()
  const { props: sortProps, sort } = useSort<FinanceReportRow, FinanceReportSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const { count, isLoading, report } = useGetGetFinanceReport({
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  })

  const columns: ColumnDef<FinanceReportRow>[] = useColumns()

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    ...usePaginationProps,
  }

  const handleStyleRow = (row: Row<FinanceReportRow>, theme: Theme): CSSObject => {
    let styles: CSSObject = {}
    if (!isParent(row)) {
      styles = {
        ...styles,
        backgroundColor: theme.palette.grey[50],
      }
    }

    return styles
  }

  return (
    <Paper>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <Table<FinanceReportRow>
        columns={columns}
        data={report}
        loading={isLoading}
        meta={{ styleRow: handleStyleRow }}
        pagination={paginationProps}
        sort={sortProps}
      />
    </Paper>
  )
}
